/*
 * Login.js — Sign-in page.
 * Route: /login
 *
 * Submits the user's email/username + password to the Express auth endpoint.
 * On success the backend returns a JWT; we pass it to AuthContext.login() which
 * stores it in localStorage so the session survives a page refresh. The user is
 * then redirected to their dashboard.
 *
 * The "email" field actually doubles as a username field — the backend accepts both.
 */

import { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { AuthContext } from "./context/AuthContext";
import API_URL from "./config";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // login() from AuthContext stores the JWT and decodes the user payload from it.
  const { login } = useContext(AuthContext);

  // POST credentials to the backend; on success store the JWT and redirect.
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // The backend accepts email OR username in the "username" field.
        body: JSON.stringify({ username: email, password }),
      });

      const data = await res.json();

      if (!res.ok || !data.token) {
        setError(data.error || "Login failed");
        return;
      }

      // Store the token via context — this also decodes the user payload.
      login(data.token);
      // Redirect admins straight to the admin dashboard;
      // everyone else lands on their user dashboard.
      try {
        const decoded = jwtDecode(data.token);
        navigate(decoded.role === "admin" ? "/admin" : "/dashboard");
      } catch {
        navigate("/dashboard");
      }
    } catch {
      setError("Server unavailable");
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-logo">
          <img src="/logo-bright-orange.svg" alt="Mixtape" className="auth-logo-img" />
          <div className="auth-logo-text">
            <span className="auth-logo-title">MIXTAPE</span>
            <span className="auth-logo-sub">Your music collection</span>
          </div>
        </div>

        <h1 className="auth-heading">SIGN IN</h1>
        <p className="auth-subheading">Welcome back</p>

        {error && <div className="auth-error">{error}</div>}

        <form className="auth-form" onSubmit={handleLogin}>
          <label className="auth-label">EMAIL OR USERNAME</label>
          <input
            className="auth-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter email or username"
          />

          <label className="auth-label">PASSWORD</label>
          <input
            className="auth-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
          />

          <div className="auth-forgot">
            <Link to="/forgot-password" className="auth-forgot-link">Forgot password?</Link>
          </div>

          <button className="auth-submit" type="submit">SIGN IN</button>
        </form>

        <p className="auth-switch">
          Don't have an account?{" "}
          <Link to="/register" className="auth-switch-link">Sign up</Link>
        </p>
      </div>
    </div>
  );
}
